import React, { useState, useEffect } from 'react';
import './CoutChart.css';

const CoutChart = ({ formulaires }) => {
  const [viewType, setViewType] = useState('bar'); // 'bar', 'pie', 'table'
  const [selectedFormulaire, setSelectedFormulaire] = useState(null);

  // Préparer les données pour les graphiques
  const chartData = formulaires
    .filter(f => f.cout_estime && f.cout_estime > 0)
    .map(f => ({
      id: f.id.slice(0, 8),
      equipement: f.equipement_zone_ligne,
      cout: parseFloat(f.cout_estime) || 0,
      probleme: f.probleme_initial,
      statut: f.statut,
      priorite: f.priorite,
      fullData: f
    }))
    .sort((a, b) => b.cout - a.cout)
    .slice(0, 20); // Top 20 des coûts

  // Données agrégées par équipement
  const equipmentData = formulaires.reduce((acc, f) => {
    if (!f.cout_estime || f.cout_estime <= 0) return acc;
    
    const equipement = f.equipement_zone_ligne || 'Non spécifié';
    const cout = parseFloat(f.cout_estime) || 0;
    
    if (!acc[equipement]) {
      acc[equipement] = { cout: 0, count: 0 };
    }
    
    acc[equipement].cout += cout;
    acc[equipement].count += 1;
    
    return acc;
  }, {});

  const equipmentChartData = Object.entries(equipmentData)
    .map(([equipement, data]) => ({
      equipement,
      cout: data.cout,
      count: data.count,
      moyenne: data.cout / data.count
    }))
    .sort((a, b) => b.cout - a.cout);

  const maxCout = Math.max(...chartData.map(d => d.cout));
  const totalCout = chartData.reduce((sum, d) => sum + d.cout, 0);

  const formatCurrency = (amount) => {
    return `${parseInt(amount).toLocaleString('fr-FR')}€`;
  };

  const getStatusColor = (statut) => {
    const colors = {
      'brouillon': '#6c757d',
      'en_cours': '#ffc107',
      'en_attente_validation': '#fd7e14',
      'valide': '#28a745',
      'rejete': '#dc3545',
      'archive': '#6c757d'
    };
    return colors[statut] || '#6c757d';
  };

  const getPriorityColor = (priorite) => {
    const colors = {
      'faible': '#20c997',
      'normale': '#ffc107',
      'haute': '#fd7e14',
      'critique': '#dc3545'
    };
    return colors[priorite] || '#6c757d';
  };

  return (
    <div className="cout-chart-container">
      {/* Contrôles de vue */}
      <div className="chart-controls">
        <button 
          className={`chart-control-btn ${viewType === 'bar' ? 'active' : ''}`}
          onClick={() => setViewType('bar')}
        >
          📊 Graphique
        </button>
        <button 
          className={`chart-control-btn ${viewType === 'pie' ? 'active' : ''}`}
          onClick={() => setViewType('pie')}
        >
          🥧 Par Équipement
        </button>
        <button 
          className={`chart-control-btn ${viewType === 'table' ? 'active' : ''}`}
          onClick={() => setViewType('table')}
        >
          📋 Tableau
        </button>
      </div>

      {/* Statistiques rapides */}
      <div className="chart-stats">
        <div className="stat-item">
          <span className="stat-label">Total analysé:</span>
          <span className="stat-value">{formatCurrency(totalCout)}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Formulaires:</span>
          <span className="stat-value">{chartData.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Coût moyen:</span>
          <span className="stat-value">{formatCurrency(totalCout / chartData.length)}</span>
        </div>
      </div>

      {/* Graphique en barres */}
      {viewType === 'bar' && (
        <div className="bar-chart">
          <h4>💰 Top Formulaires par Coût</h4>
          <div className="chart-bars">
            {chartData.slice(0, 15).map((item, index) => (
              <div key={item.id} className="bar-item">
                <div className="bar-info">
                  <span className="bar-rank">#{index + 1}</span>
                  <span className="bar-label">{item.equipement}</span>
                  <span className="bar-value">{formatCurrency(item.cout)}</span>
                </div>
                <div className="bar-container">
                  <div 
                    className="bar-fill"
                    style={{ 
                      width: `${(item.cout / maxCout) * 100}%`,
                      backgroundColor: getPriorityColor(item.priorite)
                    }}
                    onClick={() => setSelectedFormulaire(item.fullData)}
                  ></div>
                </div>
                <div className="bar-meta">
                  <span className={`status-badge status-${item.statut}`}>
                    {item.statut}
                  </span>
                  <span className="problem-preview">
                    {item.probleme.slice(0, 40)}...
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Graphique par équipement */}
      {viewType === 'pie' && (
        <div className="equipment-chart">
          <h4>🏭 Répartition par Équipement</h4>
          <div className="equipment-list">
            {equipmentChartData.map((item, index) => (
              <div key={item.equipement} className="equipment-item">
                <div className="equipment-header">
                  <span className="equipment-name">{item.equipement}</span>
                  <span className="equipment-total">{formatCurrency(item.cout)}</span>
                </div>
                <div className="equipment-details">
                  <span className="equipment-count">{item.count} formulaire(s)</span>
                  <span className="equipment-avg">Moy: {formatCurrency(item.moyenne)}</span>
                </div>
                <div className="equipment-bar">
                  <div 
                    className="equipment-bar-fill"
                    style={{ 
                      width: `${(item.cout / equipmentChartData[0].cout) * 100}%`,
                      backgroundColor: `hsl(${220 - (index * 20)}, 70%, 50%)`
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vue tableau détaillée */}
      {viewType === 'table' && (
        <div className="cost-table">
          <h4>📋 Tableau Détaillé des Coûts</h4>
          <div className="table-container">
            <table className="cost-data-table">
              <thead>
                <tr>
                  <th>Rang</th>
                  <th>Équipement</th>
                  <th>Problème</th>
                  <th>Coût</th>
                  <th>Statut</th>
                  <th>Priorité</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item, index) => (
                  <tr 
                    key={item.id} 
                    className="table-row"
                    onClick={() => setSelectedFormulaire(item.fullData)}
                  >
                    <td className="rank-cell">#{index + 1}</td>
                    <td className="equipment-cell">{item.equipement}</td>
                    <td className="problem-cell">{item.probleme}</td>
                    <td className="cost-cell">{formatCurrency(item.cout)}</td>
                    <td className="status-cell">
                      <span 
                        className="status-dot"
                        style={{ backgroundColor: getStatusColor(item.statut) }}
                      ></span>
                      {item.statut}
                    </td>
                    <td className="priority-cell">
                      <span 
                        className="priority-dot"
                        style={{ backgroundColor: getPriorityColor(item.priorite) }}
                      ></span>
                      {item.priorite}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Détail du formulaire sélectionné */}
      {selectedFormulaire && (
        <div className="formulaire-detail-popup">
          <div className="detail-popup-content">
            <div className="detail-popup-header">
              <h4>📋 Détail du Formulaire</h4>
              <button onClick={() => setSelectedFormulaire(null)}>×</button>
            </div>
            <div className="detail-popup-body">
              <p><strong>Équipement:</strong> {selectedFormulaire.equipement_zone_ligne}</p>
              <p><strong>Problème:</strong> {selectedFormulaire.probleme_initial}</p>
              <p><strong>Coût:</strong> {formatCurrency(selectedFormulaire.cout_estime)}</p>
              <p><strong>Statut:</strong> {selectedFormulaire.statut}</p>
              <p><strong>Priorité:</strong> {selectedFormulaire.priorite}</p>
              {selectedFormulaire.actions_entreprise && (
                <p><strong>Actions:</strong> {selectedFormulaire.actions_entreprise}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoutChart;


